// The coach's own phone dock card for a coaching leg: ring, answer, mute and Listen / Whisper / Stop. No hold, transfer
// or keypad: the server lists no call actions for the coach, so the engine ends this leg with a plain SIP BYE (the
// controller then ends coaching, never the call). Answer and Decline go to this device's engine only.
import type { Call } from '@bcio-voice/contracts';
import { useMemo, useState } from 'react';
import type { BridgeCall } from '../../call/bridge.ts';
import { CoachingClient } from '../../lib/api.ts';
import { capability } from '../../lib/capabilities.ts';
import { useWorkspace } from '../../state/workspace.tsx';
import { type CoachControls, coachControls } from './coaching.ts';
import { CoachControlGroup, type CoachMessage, CoachMessageLine } from './CoachControls.tsx';
import { CoachIcon } from './CoachIcons.tsx';
import { type CoachActionName, modeWord, runCoachCommand } from './monitor.ts';

export type CoachLegPhase = 'waiting' | 'ringing' | 'connecting' | 'connected';
/** The coaching leg's phase on this device, from the engine only (no engine leg yet: waiting for the INVITE). */
export function coachLegPhase(engineCall: Pick<BridgeCall, 'phase' | 'media'> | null): CoachLegPhase {
  if (!engineCall) return 'waiting';
  if (engineCall.phase === 'offered') return 'ringing';
  if (engineCall.phase === 'answering' || engineCall.media === 'connecting' || engineCall.media === 'none') return 'connecting';
  return 'connected';
}

export type CoachDockViewProps = {
  call: Call;
  phase: CoachLegPhase;
  muted: boolean;
  canMute: boolean;
  controls: CoachControls;
  busy: CoachActionName | null;
  message: CoachMessage | null;
  onAnswer: () => void;
  onDecline: () => void;
  onMute: () => void;
  onAction: (action: CoachActionName) => void;
};

export function CoachDockView({ call, phase, muted, canMute, controls, busy, message, onAnswer, onDecline, onMute, onAction }: CoachDockViewProps) {
  const mode = call.coaching?.mode ?? 'listen';
  const title = `coach-title-${call.id}`;
  const state = phase === 'ringing' ? 'Your coaching call is ringing. Answer it to hear the call.' : phase === 'waiting' ? 'Waiting for your phone to ring…' : phase === 'connecting' ? 'Connecting the call audio…' : mode === 'whisper' ? 'Whispering: the agent hears you; the other party does not.' : 'Listening: nobody on the call hears you.';
  return (
    <section className="call-card coach-card" aria-labelledby={title}>
      <p className="card-kicker"><CoachIcon name="listen" /> Coaching</p>
      <h2 id={title} className="party-name">{phase === 'connected' ? `You are ${modeWord(mode)}` : 'Coaching call'}</h2>
      <p className="party-meta" role="status" aria-live="polite">{state}</p>
      {phase === 'ringing' ? (
        <div className="pair-actions">
          <button type="button" className="btn btn-end btn-call" onClick={onDecline}><CoachIcon name="decline" /> Decline</button>
          <button type="button" className="btn btn-live btn-call" onClick={onAnswer}><CoachIcon name="answer" /> Answer</button>
        </div>
      ) : null}
      {phase === 'connected' && canMute ? (
        <button type="button" className="btn btn-control" aria-pressed={muted} onClick={onMute}><CoachIcon name={muted ? 'micOff' : 'mic'} /> {muted ? 'Unmute' : 'Mute'}</button>
      ) : null}
      <CoachControlGroup id={`coach-dock-${call.id}`} controls={controls} busy={busy} onAction={onAction} />
      <CoachMessageLine message={message} />
    </section>
  );
}

export function CoachingDockCard({ call }: { call: Call }) {
  const ws = useWorkspace();
  const client = useMemo(() => new CoachingClient(ws.api), [ws.api]);
  const [busy, setBusy] = useState<CoachActionName | null>(null);
  const [message, setMessage] = useState<CoachMessage | null>(null);
  const engineCall = ws.callControl.engineCall(call);
  const phase = coachLegPhase(engineCall);
  const deviceReady = ws.readiness.state === 'ready' && !!ws.device;
  const controls = coachControls({ capability: capability(ws.config.value, 'coaching.live') ?? undefined, userId: ws.userId, call, deviceReady, pending: busy !== null });
  const onAction = async (action: CoachActionName) => {
    if (busy) return;
    setBusy(action);
    setMessage(null);
    const outcome = await runCoachCommand(client, call.id, action, ws.device?.id ?? null);
    // Coaching already over on the server: drop this device's leg locally (a SIP BYE; the call itself is untouched).
    if (!outcome.ok && outcome.code === 'coach_not_present' && engineCall) await ws.callControl.act(call, 'hangup');
    else if (!outcome.ok) setMessage({ tone: 'failed', text: outcome.text, requestId: outcome.requestId });
    await ws.refetchCall(call.id);
    setBusy(null);
  };
  return (
    <CoachDockView
      call={call}
      phase={phase}
      muted={!!engineCall?.muted}
      canMute={!!engineCall && engineCall.microphone !== 'none'}
      controls={controls}
      busy={busy}
      message={message}
      onAnswer={() => void ws.callControl.act(call, 'answer')}
      onDecline={() => void ws.callControl.act(call, 'reject')}
      onMute={() => ws.callControl.setMuted(call, !engineCall?.muted)}
      onAction={a => void onAction(a)}
    />
  );
}
